import React, { useState, useEffect } from 'react'
import { FaEdit, FaTrash, FaTimes, FaBell, FaEye, FaSearch, FaFilter } from 'react-icons/fa'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { sendApiRequest } from '../utils/api'
import Swal from 'sweetalert2'

const NoticeList = () => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [editingNotice, setEditingNotice] = useState(null)
  const [viewingNotice, setViewingNotice] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(5)
  const [notices, setNotices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  // Fetch notices
  useEffect(() => {
    const fetchNotices = async () => {
      try {
        setLoading(true)
        const response = await sendApiRequest('api/admin/notices', {}, 'GET')
        if (response.status) {
          setNotices(response.data)
        } else {
          setError(response.message || 'Failed to fetch notices')
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch notices')
      } finally {
        setLoading(false)
      }
    }

    fetchNotices()
  }, [])

  // Filter notices based on search and filters
  const filteredNotices = notices.filter(notice => {
    const matchesSearch = notice.title.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === '' || 
      (statusFilter === 'active' && notice.isActive) || 
      (statusFilter === 'inactive' && !notice.isActive)
    return matchesSearch && matchesStatus
  })

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = filteredNotices.slice(indexOfFirstItem, indexOfLastItem)
  const totalPages = Math.ceil(filteredNotices.length / itemsPerPage)

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber)
  }

  const handleEdit = (notice) => {
    setEditingNotice(notice)
    setIsEditModalOpen(true)
  }

  const handleUpdateNotice = async (e) => {
    e.preventDefault()
    try {
      const updateResponse = await sendApiRequest(`api/admin/notices/${editingNotice._id}`, editingNotice, 'PUT')
      if (updateResponse.status) {
        setNotices(notices.map(notice => 
          notice._id === editingNotice._id ? updateResponse.data : notice
        ))
        setIsEditModalOpen(false)
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Notice updated successfully',
          background: '#f8fafc',
          color: '#1e293b',
          confirmButtonColor: '#4f46e5'
        })
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: updateResponse.message || 'Failed to update notice',
          background: '#f8fafc',
          color: '#1e293b',
          confirmButtonColor: '#4f46e5'
        })
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: err.message || 'Failed to update notice',
        background: '#f8fafc',
        color: '#1e293b',
        confirmButtonColor: '#4f46e5'
      })
    }
  }

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#4f46e5',
        cancelButtonColor: '#94a3b8',
        background: '#f8fafc',
        color: '#1e293b',
        confirmButtonText: 'Yes, delete it!'
      })

      if (result.isConfirmed) {
        const deleteResponse = await sendApiRequest(`api/admin/notices/${id}`, {}, 'DELETE')
        if (deleteResponse.status) {
          setNotices(notices.filter(notice => notice._id !== id))
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'Notice has been deleted.',
            background: '#f8fafc',
            color: '#1e293b',
            confirmButtonColor: '#4f46e5'
          })
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: deleteResponse.message || 'Failed to delete notice',
            background: '#f8fafc',
            color: '#1e293b',
            confirmButtonColor: '#4f46e5'
          })
        }
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: err.message || 'Failed to delete notice',
        background: '#f8fafc',
        color: '#1e293b',
        confirmButtonColor: '#4f46e5'
      })
    }
  }

  const handleViewContent = (notice) => {
    setViewingNotice(notice)
    setIsViewModalOpen(true)
  }

  // Handle status toggle
  const handleStatusToggle = async (notice) => {
    try {
      const updatedNotice = {
        ...notice,
        isActive: !notice.isActive
      }
      
      const response = await sendApiRequest(`api/admin/notices/${notice._id}`, updatedNotice, 'PUT')
      
      if (response.status) {
        setNotices(notices.map(n => 
          n._id === notice._id ? response.data : n
        ))
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: `Notice ${response.data.isActive ? 'activated' : 'deactivated'} successfully`,
          background: '#f8fafc',
          color: '#1e293b',
          confirmButtonColor: '#4f46e5'
        })
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: response.message || 'Failed to update notice status',
          background: '#f8fafc',
          color: '#1e293b',
          confirmButtonColor: '#4f46e5'
        })
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: err.message || 'Failed to update notice status',
        background: '#f8fafc',
        color: '#1e293b',
        confirmButtonColor: '#4f46e5'
      })
    }
  }

  if (loading) {
    return (
      <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <div className="text-indigo-700 text-lg font-medium">Loading notices...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 min-h-screen flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center">
          <div className="text-red-500 text-xl font-medium mb-4">{error}</div>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 bg-gradient-to-br from-blue-50 to-indigo-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-8 gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Notice Board</h1>
            <p className="text-sm sm:text-base text-slate-600">Manage and organize all your notices in one place</p>
          </div>
          <Link
            to="/admin-panel/add-notice"
            className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center sm:justify-start gap-2"
          >
            <FaBell className="w-4 h-4" />
            Add New Notice
          </Link>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white p-4 sm:p-6 rounded-xl mb-6 sm:mb-8 shadow-sm border border-slate-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-slate-400" />
              </div>
              <input
                type="text"
                placeholder="Search notices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 bg-slate-50 text-slate-800 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaFilter className="text-slate-400" />
              </div>
              <select 
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 bg-slate-50 text-slate-800 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            
            <div className="flex items-center justify-end sm:justify-start">
              <span className="text-xs sm:text-sm text-slate-600">
                {filteredNotices.length} {filteredNotices.length === 1 ? 'notice' : 'notices'} found
              </span>
            </div>
          </div>
        </div>

        {/* Notices Table */}
        <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr className="text-left">
                  <th className="px-6 py-4 font-medium text-slate-700">Title</th>
                  <th className="px-6 py-4 font-medium text-slate-700">Date</th>
                  <th className="px-6 py-4 font-medium text-slate-700">Status</th>
                  <th className="px-6 py-4 font-medium text-slate-700 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {currentItems.length > 0 ? (
                  currentItems.map((notice) => (
                    <tr key={notice._id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 text-slate-800 font-medium">{notice.title}</td>
                      <td className="px-6 py-4 text-slate-600">
                        {new Date(notice.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={notice.isActive}
                              onChange={() => handleStatusToggle(notice)}
                            />
                            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600">
                            </div>
                          </label>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            notice.isActive 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {notice.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end space-x-2">
                          <button 
                            onClick={() => handleViewContent(notice)}
                            className="p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="View"
                          >
                            <FaEye className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleEdit(notice)}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <FaEdit className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleDelete(notice._id)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <FaTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-slate-500">
                      <div className="flex flex-col items-center justify-center">
                        <FaBell className="w-12 h-12 text-slate-300 mb-4" />
                        <p className="text-lg font-medium">No notices found</p>
                        <p className="text-sm">Try adjusting your search or filters</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {filteredNotices.length > 0 && (
          <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-slate-600 text-sm">
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredNotices.length)} of {filteredNotices.length} entries
            </div>
            <div className="flex space-x-2">
              <button 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-white text-slate-700 rounded-lg hover:bg-slate-50 transition-colors border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                Previous
              </button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`w-8 sm:w-10 h-8 sm:h-10 flex items-center justify-center rounded-lg ${currentPage === page ? 'bg-indigo-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'} transition-colors border border-slate-200`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <button 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 sm:px-4 py-2 bg-white text-slate-700 rounded-lg hover:bg-slate-50 transition-colors border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Content Modal */}
      <AnimatePresence>
        {isViewModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-2xl"
            >
              <div className="flex justify-between items-center p-6 border-b border-slate-200">
                <h2 className="text-2xl font-bold text-slate-800">Notice Details</h2>
                <motion.button 
                  whileHover={{ scale: 1.1 }}
                  onClick={() => setIsViewModalOpen(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <FaTimes className="w-5 h-5" />
                </motion.button>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-2">Title</h3>
                  <p className="text-lg text-slate-800 font-medium">{viewingNotice?.title}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-2">Date</h3>
                    <p className="text-slate-800">
                      {new Date(viewingNotice?.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-2">Status</h3>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      viewingNotice?.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {viewingNotice?.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-2">Content</h3>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                    <p className="text-slate-700 whitespace-pre-wrap">
                      {viewingNotice?.content}
                    </p>
                  </div>
                </div>

                <div className="flex justify-end pt-4 sm:pt-6">
                  <button
                    onClick={() => setIsViewModalOpen(false)}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {isEditModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-2xl"
            >
              <div className="flex justify-between items-center p-6 border-b border-slate-200">
                <h2 className="text-2xl font-bold text-slate-800">Edit Notice</h2>
                <motion.button 
                  whileHover={{ scale: 1.1 }}
                  onClick={() => setIsEditModalOpen(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <FaTimes className="w-5 h-5" />
                </motion.button>
              </div>

              <form onSubmit={handleUpdateNotice} className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Notice Title
                  </label>
                  <input
                    type="text"
                    value={editingNotice?.title || ''}
                    onChange={(e) => setEditingNotice({
                      ...editingNotice,
                      title: e.target.value
                    })}
                    className="w-full px-4 py-3 bg-white text-slate-800 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Date
                    </label>
                    <input
                      type="date"
                      value={editingNotice?.date ? new Date(editingNotice.date).toISOString().split('T')[0] : ''}
                      onChange={(e) => setEditingNotice({
                        ...editingNotice,
                        date: e.target.value
                      })}
                      className="w-full px-4 py-3 bg-white text-slate-800 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Status
                    </label>
                    <div className="flex items-center mt-2">
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={editingNotice?.isActive || false}
                          onChange={(e) => setEditingNotice({
                            ...editingNotice,
                            isActive: e.target.checked
                          })}
                        />
                        <div className="relative w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600">
                        </div>
                        <span className="ml-3 text-sm font-medium text-slate-700">
                          {editingNotice?.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Content
                  </label>
                  <textarea
                    value={editingNotice?.content || ''}
                    onChange={(e) => setEditingNotice({
                      ...editingNotice,
                      content: e.target.value
                    })}
                    rows="6"
                    className="w-full px-4 py-3 bg-white text-slate-800 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-4 pt-4 sm:pt-6">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Update Notice
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default NoticeList;